import { useCallback, useState } from 'react';

import type { ChartV2Horizon, ChartV2Interval } from '../types/chart-v2.types';

export interface UseHorizonIntervalOptions {
  controlledHorizon?: ChartV2Horizon;
  controlledInterval?: ChartV2Interval;
  defaultHorizon: ChartV2Horizon;
  defaultInterval: ChartV2Interval;
  onHorizonChange?: (horizon: ChartV2Horizon) => void;
  onIntervalChange?: (interval: ChartV2Interval) => void;
}

export interface UseHorizonIntervalResult {
  horizon: ChartV2Horizon;
  interval: ChartV2Interval;
  handleHorizonChange: (next: ChartV2Horizon) => void;
  handleIntervalChange: (next: ChartV2Interval) => void;
}

/**
 * Standard controlled / uncontrolled pair for horizon + interval. When a
 * controlled value is provided it wins; otherwise the hook manages internal
 * state and the `handle*` callbacks both update state and emit the optional
 * consumer callback.
 */
export const useHorizonInterval = ({
  controlledHorizon,
  controlledInterval,
  defaultHorizon,
  defaultInterval,
  onHorizonChange,
  onIntervalChange,
}: UseHorizonIntervalOptions): UseHorizonIntervalResult => {
  const [internalHorizon, setInternalHorizon] = useState<ChartV2Horizon>(
    controlledHorizon ?? defaultHorizon
  );
  const [internalInterval, setInternalInterval] = useState<ChartV2Interval>(
    controlledInterval ?? defaultInterval
  );

  const horizon = controlledHorizon ?? internalHorizon;
  const interval = controlledInterval ?? internalInterval;

  const handleHorizonChange = useCallback(
    (next: ChartV2Horizon) => {
      if (controlledHorizon === undefined) {
        setInternalHorizon(next);
      }

      onHorizonChange?.(next);
    },
    [controlledHorizon, onHorizonChange]
  );

  const handleIntervalChange = useCallback(
    (next: ChartV2Interval) => {
      if (controlledInterval === undefined) {
        setInternalInterval(next);
      }

      onIntervalChange?.(next);
    },
    [controlledInterval, onIntervalChange]
  );

  return { horizon, interval, handleHorizonChange, handleIntervalChange };
};
